/**
 * useRiderStatus Hook
 * 라이더 현황 페이지의 데이터 및 상태 관리 훅
 *
 * @description
 * - 라이더 목록 조회 및 검색
 * - 계약상태별 건수 집계
 * - 클라이언트 사이드 페이지네이션
 * - 협력사 목록 조회 (본사 사용자만)
 * - 필터 상태 저장/복원
 *
 * @returns 라이더 현황 데이터 상태 및 관리 함수
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {riderStatusApi} from '../api/riderStatusApi';
import {Rider, RiderApiItem, RiderFilter, RiderSearchResponseItem, Supplier, ITEMS_PER_PAGE, STORAGE_KEYS} from '../types/riderStatus.types';
import {getItem} from '@/shared/utils/storage';
import {ContractBadge, PrepaymentBadge} from '@/shared/components/dataDisplay/Badge';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

interface ContractStatusCounts {
    total: number;
    underContract: number;
    terminatedContract: number;
    contractPending: number;
}

interface UseRiderStatusReturn {
    // 상태
    riders: Rider[];
    totalCount: number;
    contractStatusCounts: ContractStatusCounts;
    loading: boolean;
    currentPage: number;
    excelDownloading: boolean;
    totalPages: number;
    suppliers: Supplier[];
    suppliersLoading: boolean;
    filters: {
        centerId: string;
        riderName: string;
        riderId: string;
        phoneNum: string;
        contStatus: string;
    };
    userType: string | null;

    // 핸들러
    handlePageChange: (page: number) => void;
    handleRiderClick: (riderId: string) => void;
    handleFilterChange: (field: string, value: string) => void;
    handleKeyDown: (e: React.KeyboardEvent) => void;
    searchRiders: () => Promise<void>;
    handleExcelDownload: () => Promise<void>;

    // 유틸리티
    getContractBadge: (status: string) => React.ReactElement;
    getPrepaymentBadge: (prepayment: boolean) => React.ReactElement;
}

export const useRiderStatus = (): UseRiderStatusReturn => {
    const navigate = useNavigate();

    // 사용자 타입 가져오기
    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');

    const { showToast } = useToast();

    // 라이더 데이터 상태
    const [allRiders, setAllRiders] = useState<Rider[]>([]);
    const [riders, setRiders] = useState<Rider[]>([]);
    const [rawRiderData, setRawRiderData] = useState<RiderApiItem[]>([]); // 원본 API 데이터 저장
    const [loading, setLoading] = useState<boolean>(false);
    const [excelDownloading, setExcelDownloading] = useState<boolean>(false);

    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = ITEMS_PER_PAGE.DEFAULT;

    // 협력사 목록 상태
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    // 본사 사용자는 true로 시작하여 협력사 로드 완료까지 대기
    const [suppliersLoading, setSuppliersLoading] = useState<boolean>(userType === 'HEADQUARTERS');

    // 필터 상태 - 저장된 값 복원
    const [filters, setFilters] = useState(() => {
        const saved = loadFilters<{
            centerId: string;
            riderName: string;
            riderId: string;
            phoneNum: string;
            contStatus: string;
        }>(STORAGE_KEYS.RIDER_STATUS_FILTERS);
        return saved || {
            centerId: '',
            riderName: '',
            riderId: '',
            phoneNum: '',
            contStatus: ''
        };
    });

    // 기본 협력사 설정 완료 여부
    const [defaultSupplierSet, setDefaultSupplierSet] = useState(false);

    // 페이지 데이터 업데이트 (클라이언트 사이드 페이지네이션)
    const updatePageData = useCallback((data: Rider[], page: number) => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageData = data.slice(startIndex, endIndex);

        setRiders(pageData);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
        setCurrentPage(page);

    }, [itemsPerPage]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((page: number) => {
        updatePageData(allRiders, page);
    }, [allRiders, updatePageData]);

    // 라이더 클릭 핸들러
    const handleRiderClick = useCallback((riderId: string) => {
        // DashboardPage로 이동하면서 라이더 상세 페이지 표시
        navigate('/dashboard', {
            state: {
                page: 'rider_detail',
                riderId: riderId
            }
        });
    }, [navigate]);

    // 공통 데이터 매핑 함수
    const mapRiderData = useCallback((riderList: RiderApiItem[]): Rider[] => {
        return riderList.map((item: RiderApiItem) => {
            // 협력사명 매핑: API 응답에서 직접 가져오거나, suppId로 suppliers 목록에서 찾기
            let supplierName = item.centerName || item.suppName;
            if (!supplierName && item.suppId && suppliers.length > 0) {
                const supplier = suppliers.find(s => s.centerId === item.suppId);
                supplierName = supplier?.name;
            }

            return {
                id: item.riderId || '',
                name: item.name || '-',
                phoneNumber: item.phoneNumber || '-',
                joinDate: item.insDate || '-',
                contractStatusName: item.status?.contractStatusName || item.status?.description || item.contractStatusName || item.contStatus,
                deliveryMethName: item.deliveryMethName,
                insurCodeName: item.insurCodeName,
                insurStatusCodeName: item.insurStatusCodeName,
                preAcctYn: item.preAcctYn,
                supplierName: supplierName || '-',
                suppId: item.suppId,
                contractEndDate: item.contEdDate || '-',
                riderType: item.riderType,
                balAmt: item.balAmt,
                registNum: item.registNum,
                vpayRegistYn: item.vpayRegistYn
            };
        });
    }, [suppliers]);

    // 라이더 조회 (통합 함수) - filters 의존성 제거
    const fetchRiders = useCallback(async (customFilters?: RiderFilter): Promise<void> => {
        try {
            setLoading(true);

            // 필터 설정
            const searchFilters = customFilters || { ...filters };

            // SUPPLIER 사용자는 userId로 자동 필터링
            if (userType === 'SUPPLIER' && userId) {
                searchFilters.centerId = userId; // SUPPLIER의 userId를 centerId로 사용
            }

            // SUPPLIER 사용자는 항상 search API 사용 (centerId가 자동 설정됨)
            // HEADQUARTERS 사용자는 필터가 있을 때만 search API 사용
            const shouldUseSearch = userType === 'SUPPLIER' || Object.values(searchFilters).some(value => value !== '');

            let response;
            if (shouldUseSearch) {
                // 빈 값 제거 (빈 문자열, null, undefined 모두 제거)
                const cleanFilters = Object.fromEntries(
                    Object.entries(searchFilters).filter(([_, value]) =>
                        value !== '' && value !== null && value !== undefined
                    )
                );

                // 백엔드가 기대하는 필드명으로 매핑
                const mappedFilters: RiderFilter = {};

                if (cleanFilters.centerId) mappedFilters.centerId = cleanFilters.centerId;
                if (cleanFilters.riderName) mappedFilters.riderName = cleanFilters.riderName;
                if (cleanFilters.riderId) mappedFilters.riderId = cleanFilters.riderId;
                if (cleanFilters.phoneNum) mappedFilters.phoneNum = cleanFilters.phoneNum;
                // 백엔드는 contStatus를 우선 사용
                if (cleanFilters.contStatus) mappedFilters.contStatus = cleanFilters.contStatus;

                // 필터가 있거나 SUPPLIER 사용자면 search API 호출
                response = await riderStatusApi.searchRiders(mappedFilters);
            } else {
                // HEADQUARTERS 사용자이고 필터가 없으면 전체 조회
                response = await riderStatusApi.getRiderList(searchFilters);
            }

            // API 응답이 배열 형태로 직접 반환됨
            const riderList: RiderApiItem[] = Array.isArray(response) ? response : [];
            setRawRiderData(riderList); // 원본 데이터 저장
            const mappedRiders = mapRiderData(riderList);

            setAllRiders(mappedRiders);
            updatePageData(mappedRiders, 1);
        } catch (error) {
            // 조회 실패 시 빈 결과 표시
            setAllRiders([]);
            setRiders([]);
            setRawRiderData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [userType, userId, mapRiderData, updatePageData, filters]);

    // 검색용 (기존 함수명 유지)
    const searchRiders = useCallback(async (): Promise<void> => {
        setCurrentPage(1); // 검색 시 첫 페이지로 이동
        await fetchRiders(filters); // 현재 필터를 명시적으로 전달
        // 필터 저장
        saveFilters(STORAGE_KEYS.RIDER_STATUS_FILTERS, filters);
    }, [fetchRiders, filters]);

    // 협력사 목록 조회 (본사 사용자만)
    const fetchSuppliers = useCallback(async (): Promise<void> => {
        if (userType !== 'HEADQUARTERS') return;

        try {
            setSuppliersLoading(true);
            const response = await riderStatusApi.getSuppliers();

            // 응답 정규화: centerId, name, statusCode, statusDesc 사용
            let raw: RiderSearchResponseItem[] = [];

            if (Array.isArray(response)) {
                raw = response;
            } else if ('data' in response && Array.isArray(response.data)) {
                raw = response.data;
            } else if ('suppliers' in response && Array.isArray(response.suppliers)) {
                raw = response.suppliers;
            }

            const mappedSuppliers: Supplier[] = raw.map((item: RiderSearchResponseItem) => ({
                centerId: item.centerId ?? item.id ?? item.center_id ?? '',
                name: item.name ?? item.centerName ?? '',
                statusCode: typeof item.statusCode === 'string' ? item.statusCode : item.status?.code,
                statusDesc: item.statusDesc ?? item.status?.desc ?? item.statusDescription,
            }));

            setSuppliers(mappedSuppliers);
        } catch (error) {
            setSuppliers([]);
        } finally {
            setSuppliersLoading(false);
        }
    }, [userType]);

    // 필터 변경 핸들러
    const handleFilterChange = useCallback((field: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // 엔터키 검색 핸들러
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            searchRiders();
        }
    }, [searchRiders]);

    // 계약 상태 배지
    const getContractBadge = useCallback((status: string): React.ReactElement => {
        return React.createElement(ContractBadge, { status });
    }, []);

    // 선정산 배지
    const getPrepaymentBadge = useCallback((prepayment: boolean): React.ReactElement => {
        return React.createElement(PrepaymentBadge, { prepayment });
    }, []);

    // 엑셀 다운로드 핸들러
    const handleExcelDownload = useCallback(async (): Promise<void> => {
        try {
            setExcelDownloading(true);
            showToast('엑셀 다운로드를 시작합니다...', { type: ToastType.INFO });

            // 현재 필터 조건을 사용하여 엑셀 다운로드
            const searchFilters = { ...filters };

            // SUPPLIER 사용자는 userId로 자동 필터링
            if (userType === 'SUPPLIER' && userId) {
                searchFilters.centerId = userId;
            }

            // 빈 값 제거
            const cleanFilters: RiderFilter = {};
            if (searchFilters.centerId) cleanFilters.centerId = searchFilters.centerId;
            if (searchFilters.riderName) cleanFilters.riderName = searchFilters.riderName;
            if (searchFilters.riderId) cleanFilters.riderId = searchFilters.riderId;
            if (searchFilters.phoneNum) cleanFilters.phoneNum = searchFilters.phoneNum;
            if (searchFilters.contStatus) cleanFilters.contStatus = searchFilters.contStatus;

            const blob = await riderStatusApi.downloadRiderStatusExcel(cleanFilters);

            // Blob을 다운로드
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `라이더현황_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('엑셀 다운로드가 완료되었습니다.', { type: ToastType.SUCCESS });

        } catch (error) {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setExcelDownloading(false);
        }
    }, [filters, userType, userId, showToast]);

    // 초기 데이터 로드 (협력사 목록 + 라이더 목록)
    useEffect(() => {
        const initialFetch = async () => {
            // 저장된 필터가 있으면 사용
            const savedFilters = loadFilters<RiderFilter>(STORAGE_KEYS.RIDER_STATUS_FILTERS);

            // 본사 사용자이고 저장된 필터(centerId)가 없으면 협력사 로드 후 기본 협력사 설정 후 조회
            // 이 경우 초기 조회를 건너뛰고, 협력사 로드 후 useEffect에서 조회
            if (userType === 'HEADQUARTERS' && !savedFilters?.centerId) {
                setLoading(true); // 로딩 상태 유지 (협력사 로드 후 조회까지)
                return;
            }

            try {
                setLoading(true);

                const searchFilters: RiderFilter = savedFilters || {
                    centerId: '',
                    riderName: '',
                    riderId: '',
                    phoneNum: '',
                    contStatus: ''
                };

                // SUPPLIER 사용자는 userId로 자동 필터링
                if (userType === 'SUPPLIER' && userId) {
                    searchFilters.centerId = userId;
                }

                // 저장된 필터가 있거나 SUPPLIER 사용자는 search API 사용
                const shouldUseSearch = savedFilters || userType === 'SUPPLIER';

                let response;
                if (shouldUseSearch) {
                    // 빈 값 제거 (빈 문자열, null, undefined 모두 제거)
                    const cleanFilters = Object.fromEntries(
                        Object.entries(searchFilters).filter(([_, value]) =>
                            value !== '' && value !== null && value !== undefined
                        )
                    );

                    // 백엔드가 기대하는 필드명으로 매핑
                    const mappedFilters: RiderFilter = {};

                    if (cleanFilters.centerId) mappedFilters.centerId = cleanFilters.centerId;
                    if (cleanFilters.riderName) mappedFilters.riderName = cleanFilters.riderName;
                    if (cleanFilters.riderId) mappedFilters.riderId = cleanFilters.riderId;
                    if (cleanFilters.phoneNum) mappedFilters.phoneNum = cleanFilters.phoneNum;
                    if (cleanFilters.contStatus) mappedFilters.contStatus = cleanFilters.contStatus;

                    response = await riderStatusApi.searchRiders(mappedFilters);
                } else {
                    response = await riderStatusApi.getRiderList(searchFilters);
                }

                const riderList: RiderApiItem[] = Array.isArray(response) ? response : [];
                setRawRiderData(riderList); // 원본 데이터 저장
                const mappedRiders = mapRiderData(riderList);

                setAllRiders(mappedRiders);
                updatePageData(mappedRiders, 1);
            } catch {
                setAllRiders([]);
                setRiders([]);
                setRawRiderData([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };

        initialFetch();
        fetchSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, userId]);

    // suppliers가 로드되면 기존 riders 재매핑 (협력사명 업데이트)
    useEffect(() => {
        if (suppliers.length > 0 && rawRiderData.length > 0) {
            const mappedRiders = mapRiderData(rawRiderData);
            setAllRiders(mappedRiders);
            updatePageData(mappedRiders, currentPage);
        }
    }, [suppliers, rawRiderData, mapRiderData, currentPage, updatePageData]);

    // 본사 사용자: 협력사 목록 로드 후 기본 협력사 선택 및 조회
    useEffect(() => {
        // 본사 사용자이고, 협력사 로딩이 완료되었고, 아직 기본 설정을 하지 않은 경우
        if (userType === 'HEADQUARTERS' && !defaultSupplierSet && !suppliersLoading) {
            const savedFilters = loadFilters<RiderFilter>(STORAGE_KEYS.RIDER_STATUS_FILTERS);
            if (!savedFilters?.centerId && !filters.centerId) {
                if (suppliers.length > 0) {
                    const firstSupplier = suppliers[0];
                    if (firstSupplier?.centerId) {
                        // 첫 번째 협력사를 기본값으로 설정하고 해당 협력사로 조회
                        const newFilters = {
                            ...filters,
                            centerId: firstSupplier.centerId
                        };
                        setFilters(newFilters);
                        setDefaultSupplierSet(true);
                        fetchRiders(newFilters);
                    } else {
                        // centerId가 없는 경우 - 전체 조회로 풀백
                        setDefaultSupplierSet(true);
                        fetchRiders(filters);
                    }
                } else {
                    // 협력사 목록이 비어있으면 전체 조회로 풀백
                    setDefaultSupplierSet(true);
                    fetchRiders(filters);
                }
            } else {
                setDefaultSupplierSet(true);
            }
        }
    }, [userType, suppliers, suppliersLoading, defaultSupplierSet, filters, fetchRiders]);

    // 계약 상태별 건수 계산
    const contractStatusCounts = useMemo((): ContractStatusCounts => {
        const counts = allRiders.reduce((acc, rider) => {
            const status = rider.contractStatusName;
            if (status === '계약중' || status === 'UNDER_CONTRACT') {
                acc.underContract++;
            } else if (status === '계약종료' || status === 'TERMINATED_CONTRACT') {
                acc.terminatedContract++;
            } else if (status === '계약대기' || status === 'CONTRACT_PENDING') {
                acc.contractPending++;
            }
            return acc;
        }, { underContract: 0, terminatedContract: 0, contractPending: 0 });

        return {
            total: allRiders.length,
            ...counts
        };
    }, [allRiders]);

    return {
        // 상태
        riders,
        totalCount: allRiders.length,
        contractStatusCounts,
        loading,
        excelDownloading,
        currentPage,
        totalPages,
        suppliers,
        suppliersLoading,
        filters,
        userType,

        // 핸들러
        handlePageChange,
        handleRiderClick,
        handleFilterChange,
        handleKeyDown,
        searchRiders,
        handleExcelDownload,

        // 유틸리티
        getContractBadge,
        getPrepaymentBadge
    };
};
